'use client';

import { usePathname, useRouter } from 'next/navigation';
import { type FC, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from 'styled-components';

import { updateLocale as fetchUpdateLocale } from '@/api/common';
import { Button, ButtonClose } from '@/components/buttons';
import { ImageComponent } from '@/components/Image';
import { getLocaleFlagIcon } from '@/helpers/cdn';
import { countryTitle } from '@/helpers/languages';
import { getLanguageByCountry } from '@/helpers/transl';
import { useLanguageChange } from '@/modules/localization';
import { useGetCommonStore } from '@/store';
import { Spinner } from '@/styles/common';
import { ScLocalePopup } from './styled';

export type CheckLocaleProps = {
  onClose: () => void;
};

export const CheckLocale: FC<CheckLocaleProps> = ({ onClose }) => {
  const {
    t,
    i18n: { language: currentLang },
  } = useTranslation('common');
  const { mainColor, whiteColor } = useTheme();
  const pathname = usePathname();
  const { push } = useRouter();

  const [changeLanguage, pending] = useLanguageChange();
  const useCommonStore = useGetCommonStore();

  const user = useCommonStore.use.user();

  const [lang, country, flag] = useMemo(() => {
    if (user?.country == null) return [];

    const lang = getLanguageByCountry(user.country);

    if (lang == null) return [];

    return [lang, countryTitle[lang], getLocaleFlagIcon(lang)];
  }, [user?.country]);

  const handleClose = useCallback(async () => {
    await fetchUpdateLocale({ locale: currentLang });
    onClose();
  }, [onClose, currentLang]);

  const handleChangeLanguage = useCallback(async () => {
    if (lang == null) return;

    await changeLanguage(lang);

    onClose();
  }, [onClose, push, pathname, currentLang, lang, changeLanguage]);

  return (
    <ScLocalePopup data-testid="check-locale-modal">
      <div className="img-container">
        <ImageComponent
          alt="emoji"
          className="emoji"
          height={54}
          src="/emojis/thinkingEmoji.webp"
          width={54}
        />
        <ImageComponent
          alt="message"
          className="message"
          height={42}
          src="/emojis/message.webp"
          width={42}
        />
        {flag && (
          <div className="flag-img">
            <ImageComponent
              alt={`${country} flag`}
              height={16}
              src={flag}
              width={24}
            />
          </div>
        )}
      </div>
      <ButtonClose
        className="btn-close"
        data-testid="check-locale-close"
        gray
        onClick={handleClose}
        right={12}
        top={12}
      />
      <div className="text-wrapper">
        <h3 className="title">
          {t('wrongLocale')} {country}.
        </h3>
        <span className="subtitle">{t('switchLocale')}</span>
      </div>
      <Button
        className="button"
        data-testid="check-locale-yes"
        large
        onClick={handleChangeLanguage}
        primary
        wide
      >
        {pending ? (
          <Spinner background={mainColor} center color={whiteColor} size={20} />
        ) : (
          t('yes')
        )}
      </Button>
    </ScLocalePopup>
  );
};
